<template>
  <!-- Vue 2 模板保留单一根节点，两个按需弹窗均通过 append-to-body 呈现在顶层。 -->
  <div>
    <el-dialog
      :visible.sync="visible"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      custom-class="whatsapp-balance-insufficient-dialog"
      width="400px"
      append-to-body
    >
      <!-- Figma 余额不足弹窗以 400px 卡片承载告警插画、费用明细和充值入口。 -->
      <section class="whatsapp-balance-insufficient-dialog__content">
        <button
          class="whatsapp-balance-insufficient-dialog__close"
          type="button"
          :aria-label="$t('common.Close')"
          @click="closeDialog"
        >
          <img :src="closeIcon" alt="" />
        </button>
        <!-- 插画由两个 Figma SVG 图层组合，保留主图和右下角装饰的原始比例。 -->
        <div class="whatsapp-balance-insufficient-dialog__illustration" aria-hidden="true">
          <img class="whatsapp-balance-insufficient-dialog__illustration-main" :src="insufficientIllustration" alt="" />
          <img class="whatsapp-balance-insufficient-dialog__illustration-spark" :src="insufficientSpark" alt="" />
        </div>
        <h2>{{ $t('dashboard.WhatsApp-Balance-Insufficient-Title') }}</h2>
        <p class="whatsapp-balance-insufficient-dialog__description">{{ $t('dashboard.WhatsApp-Balance-Insufficient-Content') }}</p>
        <!-- WebSocket 载荷中的金额统一格式化；字段缺失时显示 --，仍保留充值动作。 -->
        <dl class="whatsapp-balance-insufficient-dialog__summary">
          <div>
            <!-- 当前余额沿用营销模块已有翻译键，避免语言包重复维护同一文案。 -->
            <dt>{{ $t('marketing.CurrentBalance') }}</dt>
            <dd class="whatsapp-balance-insufficient-dialog__amount--danger">{{ balance }}</dd>
          </div>
          <div>
            <dt>{{ $t('dashboard.WhatsApp-Estimated-Send-Cost') }}</dt>
            <dd>{{ estimatedCost }}</dd>
          </div>
        </dl>
        <!-- 关闭余额不足提示后切换至站内充值支付弹窗，无需离开当前聊天页。 -->
        <button type="button" class="whatsapp-balance-insufficient-dialog__recharge" @click="goToRecharge">
          {{ $t('dashboard.Go-Recharge') }}
        </button>
      </section>
    </el-dialog>
    <!-- 充值表单沿用团队模块的支付流程，按需挂载以避免未打开时初始化支付广播。 -->
    <RechargeDialog v-if="rechargeDialogVisible" :visible.sync="rechargeDialogVisible" :balance="rechargeBalance" />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { eventBus } from "@/main";
import EventCenter from "@/utils/EventCenter";
import RechargeDialog from "@/components/RechargeDialog.vue";
import insufficientIllustration from "@/assets/images/marketingTools/team-balance-insufficient.svg";
import insufficientSpark from "@/assets/images/marketingTools/team-balance-insufficient-spark.svg";
import closeIcon from "@/assets/images/marketingTools/dialog-close-small.svg";

const visible = ref(false);
const rechargeDialogVisible = ref(false);
const socketPayload = ref({});

// 后端可能将费用放在消息根节点或 data 中，读取两层可兼容已有 WebSocket 返回结构。
const readSocketAmount = (keys) => {
  const payload = socketPayload.value || {};
  const candidates = [payload, payload.data].filter((item) => item && typeof item === "object");

  for (const candidate of candidates) {
    for (const key of keys) {
      if (candidate[key] !== undefined && candidate[key] !== null && candidate[key] !== "") {
        return candidate[key];
      }
    }
  }

  return null;
};

// 金额仅负责展示，保留接口返回的小数位；异常或缺失的返回值不会转换为 0 以免误导用户。
const formatAmount = (value) => {
  const rawAmount = String(value).trim();
  const amount = Number(rawAmount);
  if (!Number.isFinite(amount)) return "--";

  return `$${rawAmount}`;
};

// 50001 响应以 team_amount 表示当前余额；旧字段保留兼容，避免不同接口版本展示异常。
const socketBalance = computed(() => readSocketAmount(["team_amount", "balance", "team_balance", "current_balance", "balance_available"]));
const balance = computed(() => formatAmount(socketBalance.value));
// 50001 响应以 frozen_amount 表示本次发送预估冻结金额，优先使用该字段。
const estimatedCost = computed(() => formatAmount(readSocketAmount(["frozen_amount", "estimated_cost", "estimated_amount", "estimate_amount", "cost"])));
// 充值组件只接收数值余额，异常载荷回退为 0，避免将展示格式字符串传入支付明细。
const rechargeBalance = computed(() => {
  const amount = Number(socketBalance.value);
  return Number.isFinite(amount) ? amount : 0;
});

const openDialog = (payload = {}) => {
  // 每次 50001 响应都刷新金额；visible 复用同一个实例，避免多层遮罩叠加。
  socketPayload.value = payload;
  visible.value = true;
};

const closeDialog = () => {
  // 关闭时清理上一次会话金额，下一次消息仅展示自身携带的数据。
  visible.value = false;
  socketPayload.value = {};
};

const goToRecharge = () => {
  // 保留本次余额供支付明细使用，并将余额提示切换为同站的在线充值支付弹窗。
  visible.value = false;
  rechargeDialogVisible.value = true;
};

onMounted(() => {
  // WebSocket 业务组件通过全局事件触发，弹窗不依赖当前聊天会话的生命周期。
  eventBus.$on(EventCenter.openWhatsAppBalanceInsufficientDialog, openDialog);
});

onBeforeUnmount(() => {
  // 根组件销毁或热更新时解除监听，避免同一 50001 响应触发多次。
  eventBus.$off(EventCenter.openWhatsAppBalanceInsufficientDialog, openDialog);
});
</script>

<style lang="scss">
// Element Dialog 挂载到 body，覆盖默认标题栏和外边距以让 400px 卡片保持视口居中。
.whatsapp-balance-insufficient-dialog {
  top: 50%;
  margin: 0 auto !important;
  overflow: hidden;
  border-radius: 12px;
  transform: translateY(-50%);
  background: linear-gradient(180deg, rgb(255 0 0 / 4%) 0%, rgb(255 0 0 / 0%) 29.742%), #fff;

  .el-dialog__header {
    display: none;
  }

  // 全局 el-dialog body 样式后加载时会加回左右及底部内边距，专属卡片须明确覆盖。
  &.el-dialog > .el-dialog__body {
    padding: 0;
  }

  &__content {
    position: relative;
    box-sizing: border-box;
    min-height: 466px;
    padding: 40px;
    text-align: center;
  }

  &__close {
    position: absolute;
    top: 12px;
    right: 12px;
    display: grid;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    place-items: center;
    background: transparent;
    cursor: pointer;

    img {
      width: 11.2px;
      height: 11.2px;
    }
  }

  &__illustration {
    position: relative;
    width: 120px;
    height: 120px;
    margin: 0 auto 8px;
  }

  &__illustration-main {
    position: absolute;
    top: 17.4px;
    right: 9.6px;
    width: 97.3327px;
    height: 85.0773px;
  }

  &__illustration-spark {
    position: absolute;
    right: 32.8px;
    bottom: 18.42px;
    width: 14.6007px;
    height: 14.586px;
  }

  h2 {
    margin: 0;
    color: #17181a;
    font-size: 18px;
    font-weight: 600;
    line-height: 26px;
  }

  &__description {
    width: 320px;
    margin: 16px auto;
    color: #666;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
  }

  &__summary {
    // 边框计入 320px 宽度，使明细卡与下方充值按钮左右边缘对齐。
    box-sizing: border-box;
    width: 320px;
    margin: 0 auto 24px;
    overflow: hidden;
    border: 1px solid #ededed;
    border-radius: 8px;
    background: #fafafa;
    text-align: left;

    div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: 48px;
      padding: 0 16px;
    }

    dt,
    dd {
      margin: 0;
      color: #666;
      font-size: 14px;
      font-weight: 400;
      line-height: 24px;
    }

    dd {
      color: #333;
      text-align: right;
    }
  }

  &__amount--danger {
    color: #e50000 !important;
  }

  &__recharge {
    display: block;
    width: 320px;
    min-height: 36px;
    margin: 0 auto;
    padding: 6px 16px;
    border: 0;
    border-radius: 8px;
    color: #fff;
    background: #0067ff;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    cursor: pointer;
  }
}
</style>
